import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Label, Input, Container } from 'reactstrap';
import classNames from 'classnames';
import styles from './OrderSummary.module.css';
import RemoveAddonModal from './RemoveAddonModal';
import PromoCode from './PromoCode';
import { SalesContext } from '../contexts/SalesContext';
import { useHistory, useLocation } from 'react-router';
import NJRTermsDocument from '../../assets/Documents/NJR/njr-serviceplan-terms.pdf';
import { dataLayerPush } from '../../helpers';
import InfoTooltip from '../shared/InfoTooltip/InfoTooltip';
import getClientContent from '../_BrandingProvider/brandContent';
import { phoneFormat } from '../shared/util';

const OrderSummary = ({
    // all optional
    updatePlanData,
    updateTotal,
    promoCodeUpdate,
    saveFormData,
    payAnnual,
    payAmount,
    clientId,
    showDetails = false
}) => {
    /**
     *  ==================================
     *  ===== CONTEXTS AND HOOKS =========
     *  ==================================
     */
    const history = useHistory();
    const context = useContext(SalesContext);
    const { planData, promocodeObj, isPromoApplied, taxRate, deviceData, formData } = context.data;

    const plan = planData.plan;
    const isPayAnnual = !!payAnnual;
    const { mailingAddress, firstName, lastName, email, phone } = formData;

    /**
     *  ==================================
     *  ============= STATE ==============
     *  ==================================
     */
    const [isShowRemoveModal, setIsShowRemoveModal] = useState(false);
    const [addOnToRemove, setAddOnToRemove] = useState({});
    //const taxRate = 6.625 / 100;
    const [addonsPrice, setAddonsPrice] = useState(0);
    const [totalTaxPrice, setTotalTaxPrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [planTotal, setPlanTotal] = useState(0);
    const { variableText } = getClientContent(clientId);
    const { showPromoCodeContent } = variableText?.salesConfig || false;

    /**
     *  ==================================
     *  ========== EFFECT ==============
     *  ==================================
     */
    useEffect(() => {
        showPromoCodeContent && calculateTotals();
    });

    /**
     *  ==================================
     *  ==== CONTINUE COMPONENT LOGIC ====
     *  ==================================
     */
    const calculateTotals = (promocodeObject) => {
        let addonsPriceLocal = 0;

        if (!promocodeObject) {
            promocodeObject = context.data.promocodeObj;
        }
        if (planData.selectedAddPlans.length) {
            var selectedAddPlan = isPayAnnual === true ? planData?.activeAddPlansYearly : planData?.selectedAddPlans;
            if (planData?.tsf?.name == 'higher') {
                addonsPriceLocal = selectedAddPlan?.reduce((accu, cur) => accu + cur.higherTsfPrice, 0);
            } else if (planData?.tsf?.name == 'lower') {
                addonsPriceLocal = selectedAddPlan?.reduce((accu, cur) => accu + cur.lowerTsfPrice, 0);
            } else {
                addonsPriceLocal = selectedAddPlan?.reduce((accu, cur) => accu + cur.riskProfile.totalBundlePrice, 0);
            }
        }
        addonsPriceLocal = addonsPriceLocal.toFixed(2);
        const planPrice =
            planData?.tsf?.price === null
                ? isPayAnnual === true
                    ? parseFloat(planData?.selectedPlan?.yearly || 0).toFixed(2)
                    : parseFloat(planData?.planTotal).toFixed(2)
                : isPayAnnual === true
                  ? planData?.tsf?.yearlyPrice.toFixed(2)
                  : planData?.tsf?.price.toFixed(2);
        setPlanTotal(planPrice);
        let promoPrice = 0;
        let totalPrice = 0;
        if (promocodeObject) {
            promoPrice = promocodeObject.value;

            if (promocodeObject.type == 'percent') {
                promoPrice = planPrice * (promocodeObject.value / 100);
            }
            if (promocodeObj) {
                promocodeObj.promoPrice = promoPrice.toFixed(2);
            }
        }

        const basePlan = parseFloat((planPrice - parseFloat(promoPrice)).toFixed(2));

        const totalTax = parseFloat(((basePlan + parseFloat(addonsPriceLocal)) * parseFloat(taxRate)).toFixed(2));
        totalPrice = parseFloat(basePlan + parseFloat(addonsPriceLocal) + totalTax).toFixed(2);
        setAddonsPrice(addonsPriceLocal);
        setTotalTaxPrice(totalTax);
        setTotalPrice(totalPrice);
        if (payAmount) {
            payAmount(totalPrice);
        }
        return totalPrice;
    };

    const handleRemoveClick = (e, addon) => {
        e.preventDefault();
        setIsShowRemoveModal(true);
        setAddOnToRemove(addon);
    };

    const removeAddonHandler = () => {
        planData.selectedAddPlans = planData.selectedAddPlans.filter((ad, i) => addOnToRemove.title != ad.title);
        planData.activeAddPlansYearly = planData?.activeAddPlansYearly?.filter(
            (ad, i) => addOnToRemove.title != ad.title
        );
        planData.activeAddPlans = planData.activeAddPlans.filter((ad) => addOnToRemove.name != ad);
        planData.additionalTotal -=
            planData?.tsf?.name == 'lower'
                ? addOnToRemove?.lowerTsfPrice
                : planData?.tsf?.name == 'higher'
                  ? addOnToRemove?.higherTsfPrice
                  : addOnToRemove.riskProfile.totalBundlePrice;

        dataLayerPush('remove_item', {
            ecommerce: {
                currency: 'USD',
                value: addOnToRemove.promoPrice,
                items: [
                    {
                        item_name: addOnToRemove.title,
                        item_id: addOnToRemove.name,
                        price: plan === 'monthly' ? addOnToRemove.monthly : addOnToRemove.yearly,
                        item_list_id: 'selectedAddPlans',
                        item_category: 'add_on_plan'
                    }
                ]
            }
        });

        context.updatePlanData(planData);
        let amount = calculateTotals();
        if (updateTotal) {
            updateTotal(amount);
        }

        if (updatePlanData) {
            var selectedAddPlans = [];
            var addOns = planData.selectedAddPlans.map((i) => i.riskProfile);
            for (var i = 0; i < addOns.length; i++) {
                selectedAddPlans.push({ riskProfile: addOns[i] });
            }

            var updatedPlanData = {
                activeAddPlans: planData.selectedAddPlans.map((i) => i.riskProfile),
                activeAddPlansYearly: planData?.activeAddPlansYearly.map((i) => i.riskProfile),
                planData: {
                    selectedPlan: {
                        riskProfile: planData.selectedPlan?.riskProfile
                    },
                    selectedAddPlans: selectedAddPlans,
                    activeAddPlansYearly: planData?.activeAddPlansYearly
                }
            };

            updatePlanData(updatedPlanData);
        }
        setIsShowRemoveModal(false);
    };

    const hideAddonRemoveModalHandler = () => {
        setIsShowRemoveModal(false);
        setAddOnToRemove({});
    };

    const onEditPlanHandler = (event) => {
        event.preventDefault();
        if (saveFormData) {
            saveFormData();
        }
        history.push('/shopplan');
    };

    const handleApplyPromoCode = (obj) => {
        dataLayerPush('select_promotion', {
            ecommerce: {
                items: [
                    {
                        coupon: obj.name,
                        discount: parseFloat(Number(obj.promoPrice)).toFixed(2)
                    }
                ]
            }
        });
        context.updatePromoCode(obj);
        if (promoCodeUpdate) {
            promoCodeUpdate(obj);
        }

        let amount = calculateTotals(obj);
        if (updateTotal) {
            updateTotal(amount);
        }
    };

    const removePromoCode = (event) => {
        event.preventDefault();
        context.updatePromoCode(null);
        let amount = calculateTotals({ type: '', value: 0 });
        if (updateTotal) {
            updateTotal(amount);
        }
    };

    const getTotalAmount = () => {
        const planPrice = parseFloat(deviceData?.planPrice ?? 0);
        const tax = parseFloat(deviceData?.taxAmount ?? 0);
        const total = planPrice + tax;

        return <span>${total.toFixed(2)}</span>;
    };

    const onEditProductInfo = (e) => {
        e.preventDefault();
        if (saveFormData) {
            saveFormData();
        }
        history.push('/buy/protectionplan');
    };

    const onEditDetails = (e) => {
        e.preventDefault();
        history.push('/customerinfo');
    };

    return (
        <Container className={styles.orderSummaryContainer}>
            <h2 className={classNames('h5', styles.orderSummaryHeading)}>Order Summary</h2>
            {showPromoCodeContent ? (
                <>
                    <Row>
                        <Col>
                            <Row className='mb-3'>
                                <Col className='col-9'>
                                    {planData?.selectedPlan?.title}
                                    <a
                                        href='#'
                                        onClick={onEditPlanHandler}
                                        className='ml-2'
                                    >
                                        Edit
                                    </a>
                                </Col>
                                <Col className={` ${styles.textAlignRight} col-3`}>{`$${planTotal}`}</Col>
                            </Row>

                            {isPayAnnual === false
                                ? planData.selectedAddPlans.map((addon, index) => (
                                      <Row
                                          className='mb-3'
                                          key={index}
                                      >
                                          <Col className='col-9'>
                                              <span>
                                                  {addon.title}
                                                  <a
                                                      href='#'
                                                      onClick={(e) => handleRemoveClick(e, addon)}
                                                      className='ml-2'
                                                  >
                                                      Remove
                                                  </a>
                                              </span>
                                          </Col>
                                          <Col
                                              className={` ${styles.textAlignRight} col-3`}
                                          >{`$${planData?.tsf?.name == 'lower' ? addon?.lowerTsfPrice.toFixed(2) : planData?.tsf?.name == 'higher' ? addon?.higherTsfPrice.toFixed(2) : addon?.riskProfile?.totalBundlePrice?.toFixed(2)}`}</Col>
                                      </Row>
                                  ))
                                : planData.activeAddPlansYearly.map((addon, index) => (
                                      <Row
                                          className='mb-3'
                                          key={index}
                                      >
                                          <Col className='col-9'>
                                              <span>
                                                  {addon.title}
                                                  <a
                                                      href='#'
                                                      onClick={(e) => handleRemoveClick(e, addon)}
                                                      className='ml-2'
                                                  >
                                                      Remove
                                                  </a>
                                              </span>
                                          </Col>
                                          <Col
                                              className={` ${styles.textAlignRight} col-3`}
                                          >{`$${planData?.tsf?.name == 'lower' ? addon?.lowerTsfPrice.toFixed(2) : planData?.tsf?.name == 'higher' ? addon?.higherTsfPrice.toFixed(2) : addon?.riskProfile?.totalBundlePrice?.toFixed(2)}`}</Col>
                                      </Row>
                                  ))}
                            {promocodeObj && (
                                <Row className='mb-3'>
                                    <Col className='col-9'>
                                        <span>
                                            {promocodeObj.name}
                                            <a
                                                href='#'
                                                onClick={removePromoCode}
                                                className='ml-2'
                                            >
                                                Remove
                                            </a>
                                        </span>
                                    </Col>
                                    <Col
                                        className={` ${styles.textAlignRight} col-3`}
                                    >{`-$${promocodeObj.promoPrice}`}</Col>
                                </Row>
                            )}

                            <Row className='mb-3'>
                                <Col className='col-9'>
                                    <span>Tax</span>
                                </Col>
                                <Col
                                    className={` ${styles.textAlignRight} col-3`}
                                >{`$${totalTaxPrice.toFixed(2)}`}</Col>
                            </Row>
                            <Row className='mb-5'>
                                <Col className='col-9'>Total </Col>
                                <Col className={` ${styles.textAlignRight} col-3`}>{`$${totalPrice}`}</Col>
                            </Row>
                        </Col>
                    </Row>

                    <PromoCode
                        handleApplyPromoCode={handleApplyPromoCode}
                        isPromoApplied={isPromoApplied}
                    />
                    <Row className={classNames('mb-3', styles.dueContainer)}>
                        <Col>Due Today:</Col>
                        <Col col='6'>{`$${totalPrice}`}</Col>
                    </Row>
                    <Row>
                        <Col col='6'>
                            <p className='mb-0'>
                                {isPayAnnual === false
                                    ? 'Payments will be automatically charged to the card provided every thirty (30) '
                                      + 'days until the subscription is canceled. Further details can be found in the'
                                    : 'Plans will automatically renew annually, but can be canceled anytime.'
                                      + ' Further details can be found in the'}
                                <a
                                    onClick={() => window.open(NJRTermsDocument)}
                                    className={classNames('ml-1', styles.link)}
                                >
                                    terms and conditions.
                                </a>
                            </p>
                        </Col>
                    </Row>
                </>
            ) : (
                <Row>
                    <Col className={styles.standAloneWrapper}>
                        <Row className='mb-3'>
                            <Col className='col-9'>{deviceData?.activePlan?.riskProfileDescription}</Col>
                            <Col className={`${styles.textAlignRight} col-3`}>${deviceData?.planPrice?.toFixed(2)}</Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col className='col-9'>
                                {showDetails ? (
                                    <span>Tax:</span>
                                ) : (
                                    <>
                                        Estimated Tax:
                                        <span className={styles.infoIcon}>
                                            <InfoTooltip
                                                customClass={styles.infoText}
                                                infoType='questionInfo'
                                                message='Sales tax is only an estimate. Final sales tax will be calculated based on your address during check out.'
                                            />
                                        </span>
                                    </>
                                )}
                            </Col>
                            <Col className={`${styles.textAlignRight} col-3`}>
                                {deviceData?.taxAmount !== null ? (
                                    <span>${deviceData?.taxAmount?.toFixed(2)}</span>
                                ) : (
                                    '---'
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col className='col-9 font-weight-bold'>Total</Col>
                            <Col className={`${styles.textAlignRight} col-3 font-weight-bold`}>{getTotalAmount()}</Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col className='col-9'>
                                <h4 className={styles.orderSummaryHeader}>Product Info</h4>
                            </Col>
                            <Col className={`${styles.textAlignRight} col-3`}>
                                <a
                                    href='#'
                                    onClick={onEditProductInfo}
                                >
                                    Edit
                                </a>
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                {deviceData?.deviceInfo?.map((info) => (
                                    <>
                                        <p className='mb-0 font-weight-bold'>{info.assetName}</p>
                                        <p>Serial Number: {info.serial}</p>
                                    </>
                                ))}
                            </Col>
                        </Row>
                        <hr />
                        {showDetails && (
                            <>
                                <Row>
                                    <Col className='col-9'>
                                        <h4 className={styles.orderSummaryHeader}>Your Details</h4>
                                    </Col>
                                    <Col className={`${styles.textAlignRight} col-3`}>
                                        <a
                                            href='#'
                                            onClick={onEditDetails}
                                        >
                                            Edit
                                        </a>
                                    </Col>
                                </Row>
                                <hr />
                                <Row className={styles.detailsContainer}>
                                    <Col>
                                        <ul className='noBullets'>
                                            <li>
                                                {firstName || ''} {lastName || ''}
                                            </li>
                                            <li>{mailingAddress.address1 || ''}</li>
                                            <li>{mailingAddress.address2 || ''}</li>
                                            <li>
                                                {mailingAddress.city || ''}, {mailingAddress.state || ''}{' '}
                                                {mailingAddress.zip || ''}
                                            </li>
                                            <li>{phoneFormat(phone)}</li>
                                            <li>{email}</li>
                                        </ul>
                                    </Col>
                                </Row>
                                <hr />
                            </>
                        )}
                        <Row>
                            <Col className='col-9'>
                                <h4 className={styles.orderSummaryHeader}>Due Today:</h4>
                            </Col>
                            <Col className={`col-3 ${styles.textAlignRight} ${styles.dueAmount}`}>
                                {getTotalAmount()}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            )}

            {isShowRemoveModal && (
                <RemoveAddonModal
                    addon={addOnToRemove}
                    handlerRemoveAddon={removeAddonHandler}
                    hideModal={hideAddonRemoveModalHandler}
                />
            )}
        </Container>
    );
};

export default OrderSummary;
